import React, { forwardRef, memo } from 'react';

import type { CompiledStyles } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { type BaseCellProps } from '../../ui/primitives/base-cell';
import { TD } from '../../ui/primitives/td';
import { type RowSpacing, TableType } from '../charlie-table.types';

const cellBackgroundStyles: {
    readonly success: CompiledStyles<{
        backgroundColor: "var(--ds-background-success)";
    }>; readonly danger: CompiledStyles<{
        backgroundColor: "var(--ds-background-danger)";
    }>; readonly information: CompiledStyles<{
        backgroundColor: "var(--ds-background-information)";
    }>; readonly discovery: CompiledStyles<{
        backgroundColor: "var(--ds-background-discovery)";
    }>; readonly warning: CompiledStyles<{
        backgroundColor: "var(--ds-background-warning)";
    }>; readonly selected: CompiledStyles<{
        backgroundColor: "var(--ds-background-selected)";
    }>; readonly disabled: CompiledStyles<{
        // Using 'input.hovered' to avoid any alpha transparency issues with the background color (which affects pinning, for example). Other Neutral100's are too dark in dark mode.
        backgroundColor: "var(--ds-background-input-hovered)";
    }>;
} = cssMap({
	success: {
		backgroundColor: token('color.background.success'),
	},
	danger: {
		backgroundColor: token('color.background.danger'),
	},
	information: {
		backgroundColor: token('color.background.information'),
	},
	discovery: {
		backgroundColor: token('color.background.discovery'),
	},
	warning: {
		backgroundColor: token('color.background.warning'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
	},
	disabled: {
		// Using 'input.hovered' to avoid any alpha transparency issues with the background color (which affects pinning, for example). Other Neutral100's are too dark in dark mode.
		backgroundColor: token('color.background.input.hovered'),
	},
});

type CellBackgroundKeys = keyof typeof cellBackgroundStyles;

const styles = cssMap({
	InputCell: {
		borderInlineEndWidth: token('border.width'),
		borderInlineEndStyle: 'solid',
		borderInlineEndColor: token('color.border'),
	},

	RowCell: {
		paddingInlineStart: token('space.200'),
		display: 'flex',
		height: '100%',
		alignItems: 'center',
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		borderInlineEndStyle: 'solid',
		paddingBlock: token('space.0'),
		whiteSpace: 'nowrap',
	},
	defaultRowHeight: {
		minHeight: '45px',
	},
	compactRowHeight: {
		minHeight: '36px',
	},
	overflowHiddenStyle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

export type RowCellProps = {
	columnId: string;
	tableType?: TableType;
	cellBackground?: CellBackgroundKeys;
	rowSpacing?: RowSpacing;
} & Omit<BaseCellProps, 'as'>;
/**
 * __RowCell__
 *
 * A table row cell. A styled `td` element.
 *
 */
export const RowCell: React.MemoExoticComponent<React.ForwardRefExoticComponent<{
    columnId: string;
    tableType?: TableType;
    cellBackground?: CellBackgroundKeys;
    rowSpacing?: RowSpacing;
} & Omit<BaseCellProps, "as"> & React.RefAttributes<HTMLTableCellElement>>> = memo(
	forwardRef<HTMLTableCellElement, RowCellProps>(
		(
			{
				children,
				testId,
				xcss,
				columnId,
				tableType = TableType.input,
				style,
				cellBackground,
				rowSpacing,
				...props
			},
			ref,
		) => {
			return (
				<TD
					testId={testId}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{
						background: !cellBackground ? 'inherit' : undefined,
						zIndex: `var(--col-${columnId}-zIndex)`,
						left: `var(--col-${columnId}-left)`,
						right: `var(--col-${columnId}-right)`,
						position:
							`var(--col-${columnId}-position)` as unknown as React.CSSProperties['position'],
						width: `var(--col-${columnId}-size)`,
						borderInlineEndWidth: `var(--col-${columnId}-resizing-border-width, var(--col-${columnId}-hover-border-width, var(--charlie-col-border-width, ${tableType === TableType.input ? token('border.width') : '0px'})))`,
						borderInlineEndColor: `var(--col-${columnId}-resizing-border-color, var(--col-${columnId}-hover-border-color, ${token('color.border')}))`,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						...style,
					}}
					xcss={cx(
						styles.RowCell,
						tableType === TableType.input && styles.InputCell,
						cellBackground && cellBackgroundStyles[cellBackground],
						styles.overflowHiddenStyle,
						rowSpacing === 'compact' ? styles.compactRowHeight : styles.defaultRowHeight,
						xcss,
					)}
					ref={ref}
					{...props}
				>
					{children}
				</TD>
			);
		},
	),
);
